import {
  useCombobox,
  useMultipleSelection,
  type UseComboboxState,
  type UseComboboxStateChangeOptions,
} from "downshift";
import Portal from "@ui-kit/other/Portal/Portal.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  KeyboardEvent,
} from "react";
import Dropdown from "@ui-kit/data-display/Dropdown/Dropdown.web";
import List from "@ui-kit/data-display/List/List.web";
import ListItem from "@ui-kit/data-display/ListItem/ListItem.web";
import Input, {
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from "@ui-kit/form/Input/Input.web";
import InputClearButton from "@ui-kit/form/InputClearButton/InputClearButton.web";
import MultiSelectedListItems from "@ui-kit/form/MultiSelectedListItems/MultiSelectedListItems.web";
import Icon from "@ui-kit/icons/Icon.web";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";
import VisuallyHidden from "@ui-kit/other/VisuallyHidden/VisuallyHidden.web";
import { useTranslation } from "@shared/utils/internationalization";
import type {
  ComboBoxProps,
  ComboBoxInputItemType,
  ComboBoxMultiSelectProps,
  ComboBoxSingleSelectProps,
} from "./ComboBox.props";

export type {
  ComboBoxProps,
  ComboBoxInputItemType,
  ComboBoxSingleSelectProps,
  ComboBoxMultiSelectProps,
  ComboBoxBaseProps,
} from "./ComboBox.props";

const MENU_OFFSET = 4;
const DROPDOWN_Z_INDEX = 1600; // Above Chakra overlay (1300), modal (1400), popover (1500)

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  placement: "bottom" | "top";
}

function ComboBox<T extends ComboBoxInputItemType = ComboBoxInputItemType>(
  props: ComboBoxProps<T>
): JSX.Element {
  // Base props
  const {
    id,
    placeholder,
    items,
    ariaLabel,
    label,
    hideLabel = false,
    variant = "outline",
    dropdownVariant = "secondary",
    InputLeftElementIcon,
    listIcon,
    isMobileModal = false,
    onClearClick,
    initialInputValue = "",
    hoverTextColor = "fg.inverted",
    renderItem,
    onInputValueChange,
    isLoading: isLoadingProp = false,
    hasNextPage = false,
    onLoadMore,
    isFetchingNextPage = false,
    serverPaginated,
    maxDropdownItems = 10,
    isItemDisabled,
    disabledItemText,
    defaultHighlightedIndex = 0,
    multiple,
    helperText,
  } = props;

  // Multi-select specific props (only available when multiple=true)
  const {
    excludeSelected: excludeSelectedProp,
    selectedItemsDisplay = "tags",
    hideDropdown = false,
    creatable = false,
    validateNewItem,
    createItem,
    renderSelected,
    onItemAdd,
    onItemRemove,
    value: multiValue,
    onChange: onMultiChange,
  } = (multiple ? props : {}) as Partial<ComboBoxMultiSelectProps<T>>;
  const excludeSelected = multiple ? excludeSelectedProp ?? true : false;

  // Single-select specific props (only available when multiple=false/undefined)
  const {
    excludeItems,
    resetOnSelect = false,
    value: singleValue,
    onChange: onSingleChange,
  } = (!multiple ? props : {}) as Partial<ComboBoxSingleSelectProps<T>>;

  const { t } = useTranslation("common");

  // Combine loading states - parent can use either prop
  const isLoading = isLoadingProp || isFetchingNextPage;

  // Local input value state (for filtering) — keeps the items array passed to
  // useCombobox in sync with the items rendered in the dropdown.
  const [multiInputValue, setMultiInputValue] = useState(initialInputValue);
  const [singleInputValue, setSingleInputValue] = useState(initialInputValue);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Dropdown position state (for fixed positioning inside Portal)
  const [dropdownPosition, setDropdownPosition] =
    useState<DropdownPosition | null>(null);

  // Multi-select state
  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
  } = useMultipleSelection<T>({
    selectedItems: multiple ? multiValue ?? [] : [],
    onSelectedItemsChange: ({ selectedItems: newItems }) => {
      if (multiple && onMultiChange) {
        onMultiChange(newItems ?? []);
      }
    },
  });

  // Server-paginated dropdowns rely on the backend for filtering and page size.
  // Client-paginated dropdowns can still use the same Load More affordance while
  // keeping local filtering and item limiting enabled. When `serverPaginated`
  // is not explicitly set, fall back to the legacy auto-detection (presence of
  // `onLoadMore`) so existing consumers continue to bypass client-side filtering
  // and `maxDropdownItems` truncation.
  const isServerPaginated = serverPaginated ?? Boolean(onLoadMore);

  // Filter items for dropdown
  const getFilteredItems = useCallback(
    (currentInputValue: string): T[] => {
      let filteredItems = [...items];

      // Only apply client-side name filter when NOT using server-side search.
      // When server-paginated, the server already filters by the query.
      if (currentInputValue && !isServerPaginated) {
        filteredItems = filteredItems.filter((item) =>
          (item.name ?? "")
            .toLowerCase()
            .includes(currentInputValue.toLowerCase())
        );
      }

      // Exclude selected items in multi-select mode
      if (multiple && excludeSelected) {
        const selectedIds = selectedItems.map((i) => i.id);
        filteredItems = filteredItems.filter(
          (item) => !selectedIds.includes(item.id)
        );
      }

      // Exclude specific items in single-select mode
      if (!multiple && excludeItems) {
        const excludeIds = excludeItems.map((i) => i.id);
        filteredItems = filteredItems.filter(
          (item) => !excludeIds.includes(item.id)
        );
      }

      // Only apply client-side item limit when NOT using server-side pagination.
      // When server-paginated, the "Load More" button controls how many items
      // are fetched, so truncating here would discard items the user requested.
      if (!isServerPaginated && filteredItems.length > maxDropdownItems) {
        filteredItems.length = maxDropdownItems;
      }

      return filteredItems;
    },
    [
      items,
      multiple,
      excludeSelected,
      selectedItems,
      excludeItems,
      maxDropdownItems,
      isServerPaginated,
    ]
  );

  // Combobox state
  const comboboxConfig = multiple
    ? {
        id,
        items: getFilteredItems(multiInputValue),
        inputValue: multiInputValue,
        defaultHighlightedIndex,
        itemToString: (item: T | null) => item?.name || "",
        onInputValueChange: ({
          inputValue: newInputValue,
        }: {
          inputValue?: string;
        }) => {
          setMultiInputValue(newInputValue ?? "");
          if (onInputValueChange) {
            onInputValueChange(newInputValue ?? "");
          }
        },
        onSelectedItemChange: ({
          selectedItem,
        }: {
          selectedItem?: T | null;
        }) => {
          if (!selectedItem) return;
          if (onItemAdd) {
            onItemAdd(selectedItem);
          }
          addSelectedItem(selectedItem);
          setMultiInputValue("");
        },
        stateReducer: (
          state: UseComboboxState<T>,
          actionAndChanges: UseComboboxStateChangeOptions<T>
        ) => {
          const { changes, type } = actionAndChanges;
          switch (type) {
            case useCombobox.stateChangeTypes.InputKeyDownEnter:
            case useCombobox.stateChangeTypes.ItemClick: {
              const clickedItem = changes.selectedItem;
              // If downshift would skip onSelectedItemChange because the
              // same item is being re-selected (reference equality), fire
              // the add callback manually via a microtask.
              if (clickedItem && clickedItem === state.selectedItem) {
                queueMicrotask(() => {
                  onItemAdd?.(clickedItem);
                  addSelectedItem(clickedItem);
                  setMultiInputValue("");
                });
              }
              return {
                ...changes,
                isOpen: state.isOpen,
                highlightedIndex: state.highlightedIndex,
                inputValue: "", // Clear input after selection in multi-select
              };
            }
            case useCombobox.stateChangeTypes.InputBlur:
              // Don't set input to selected item on blur in multi-select
              return {
                ...changes,
                inputValue: state.inputValue,
              };
            default:
              return changes;
          }
        },
      }
    : {
        id,
        items: getFilteredItems(singleInputValue),
        ...(singleValue !== undefined
          ? { selectedItem: singleValue ?? null }
          : { initialInputValue }),
        defaultHighlightedIndex,
        itemToString: (item: T | null) => {
          if (item?.secondaryText) {
            return `${item.name}, ${item.secondaryText}`;
          }
          return item?.name || "";
        },
        onInputValueChange: ({
          inputValue: newInputValue,
        }: {
          inputValue?: string;
        }) => {
          setSingleInputValue(newInputValue ?? "");
          if (onInputValueChange) {
            onInputValueChange(newInputValue ?? "");
          }
        },
        onSelectedItemChange: ({
          selectedItem,
        }: {
          selectedItem?: T | null;
        }) => {
          onSingleChange?.(selectedItem ?? null);
        },
        stateReducer: (
          state: UseComboboxState<T>,
          actionAndChanges: UseComboboxStateChangeOptions<T>
        ) => {
          const { changes, type } = actionAndChanges;
          switch (type) {
            case useCombobox.stateChangeTypes.InputKeyDownEnter:
            case useCombobox.stateChangeTypes.ItemClick: {
              const clickedItem = changes.selectedItem;
              // If downshift would skip onSelectedItemChange because the
              // same item is being re-selected (reference equality), fire
              // the callback manually via a microtask.
              if (clickedItem && clickedItem === state.selectedItem) {
                queueMicrotask(() => {
                  onSingleChange?.(clickedItem);
                });
              }
              return {
                ...changes,
                ...(resetOnSelect && { inputValue: "" }),
              };
            }
            default:
              return changes;
          }
        },
      };

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getLabelProps,
    highlightedIndex,
    getItemProps,
    reset,
    selectItem,
    closeMenu,
    inputValue: downshiftInputValue,
  } = useCombobox<T>(comboboxConfig as Parameters<typeof useCombobox<T>>[0]);

  const currentInputValue = multiple ? multiInputValue : downshiftInputValue;
  const filteredItems = useMemo(
    () => getFilteredItems(currentInputValue),
    [getFilteredItems, currentInputValue]
  );

  // Calculate dropdown position relative to viewport
  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = Math.min(filteredItems.length * 48 + 32, 320);

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const placement: "bottom" | "top" =
      spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove
        ? "bottom"
        : "top";

    return {
      top:
        placement === "bottom"
          ? rect.bottom + MENU_OFFSET
          : rect.top - MENU_OFFSET,
      left: rect.left,
      width: rect.width,
      placement,
    };
  }, [filteredItems.length]);

  // Update position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setDropdownPosition(calculatePosition());
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen, calculatePosition]);

  // Close on scroll (standard UX pattern)
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleScroll = (e: Event) => {
      // Don't close if scrolling inside the dropdown itself
      const target = e.target as HTMLElement;
      if (target.closest?.('[data-combobox-menu="true"]')) {
        return;
      }
      closeMenu();
    };

    // Use capture phase to catch scroll events from any ancestor
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isOpen, closeMenu]);

  // Close on window resize
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleResize = () => closeMenu();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen, closeMenu]);

  // Handle creatable mode (Enter to create new item)
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === "Enter" &&
      multiple &&
      creatable &&
      multiInputValue &&
      !multiInputValue.includes(",")
    ) {
      // Validate if validator provided
      if (validateNewItem && !validateNewItem(multiInputValue)) {
        return;
      }

      event.preventDefault();

      // Create item
      const newItem = createItem
        ? createItem(multiInputValue)
        : ({ id: multiInputValue.toUpperCase(), name: multiInputValue } as T);

      if (onItemAdd) {
        onItemAdd(newItem);
      }
      addSelectedItem(newItem);
      setMultiInputValue("");
      selectItem(null as unknown as T);
      closeMenu();
    }
  };

  // Handle removing selected item
  const handleRemoveSelectedItem = (item: T) => {
    if (onItemRemove) {
      onItemRemove(item);
    }
    removeSelectedItem(item);
  };

  // Always call getDropdownProps so downshift can track the ref properly
  const dropdownProps = getDropdownProps({
    preventKeyAction: multiple ? isOpen : undefined,
  });
  const inputProps = getInputProps(dropdownProps);
  const menuProps = getMenuProps();

  // Merge input ref
  const mergedInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      // Forward to downshift's ref
      const downshiftRef = (inputProps as { ref?: React.Ref<HTMLInputElement> })
        .ref;
      if (typeof downshiftRef === "function") {
        downshiftRef(node);
      } else if (downshiftRef && typeof downshiftRef === "object") {
        (
          downshiftRef as React.MutableRefObject<HTMLInputElement | null>
        ).current = node;
      }
    },
    [inputProps]
  );

  // Merge dropdown ref with downshift's menu ref
  const mergedDropdownRef = useCallback(
    (node: HTMLDivElement | null) => {
      dropdownRef.current = node;
      const downshiftRef = (menuProps as { ref?: React.Ref<HTMLDivElement> })
        .ref;
      if (typeof downshiftRef === "function") {
        downshiftRef(node);
      } else if (downshiftRef && typeof downshiftRef === "object") {
        (
          downshiftRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = node;
      }
    },
    [menuProps]
  );

  const handleClear = useCallback(() => {
    if (multiple) {
      setMultiInputValue("");
    } else {
      setSingleInputValue("");
    }
    reset();
    inputRef.current?.focus();
    if (onClearClick) {
      onClearClick();
    }
  }, [reset, onClearClick, multiple]);

  // Input container for multi-select mode
  const MultiSelectInputContainer = (
    <Box
      width="100%"
      outline="2px solid transparent"
      borderWidth="1px"
      borderColor="gray.400"
      borderRadius="sm"
      background="bg"
      color="fg"
      _focusWithin={{
        outlineColor: "primary.600",
        outlineWidth: "2px",
        borderColor: "transparent",
      }}
    >
      {selectedItemsDisplay === "tags" && selectedItems.length > 0 && (
        <MultiSelectedListItems<T>
          selectedItems={selectedItems}
          match={renderSelected}
          getSelectedItemProps={getSelectedItemProps}
          removeSelectedItem={handleRemoveSelectedItem}
        />
      )}
      <InputGroup width="100%">
        {InputLeftElementIcon && (
          <InputLeftElement pointerEvents="none">
            {InputLeftElementIcon}
          </InputLeftElement>
        )}
        <Input
          {...inputProps}
          ref={mergedInputRef}
          required={false}
          pl={InputLeftElementIcon ? "48px" : "16px"}
          variant={variant}
          borderWidth="0"
          outline="none"
          _focus={{ boxShadow: "none" }}
          aria-label={hideLabel ? ariaLabel : undefined}
          placeholder={placeholder}
          isTruncated
          tabIndex={0}
          onKeyDown={handleKeyDown}
        />
        {currentInputValue ? (
          <InputRightElement>
            <InputClearButton
              tabIndex={-1}
              onClick={handleClear}
              aria-label={t("Clear selection")}
            />
          </InputRightElement>
        ) : null}
      </InputGroup>
    </Box>
  );

  // Input container for single-select mode
  const SingleSelectInputContainer = (
    <InputGroup aria-label={ariaLabel}>
      {InputLeftElementIcon && (
        <InputLeftElement pointerEvents="none">
          {InputLeftElementIcon}
        </InputLeftElement>
      )}
      <Input
        {...inputProps}
        ref={mergedInputRef}
        pl={InputLeftElementIcon ? "36px" : "16px"}
        aria-label={ariaLabel}
        placeholder={placeholder}
        isTruncated
        variant={variant}
      />
      {currentInputValue && !isMobileModal ? (
        <InputRightElement>
          <InputClearButton
            tabIndex={-1}
            onClick={handleClear}
            aria-label="clear selection"
          />
        </InputRightElement>
      ) : null}
    </InputGroup>
  );

  // Should show dropdown
  const shouldShowDropdown =
    !hideDropdown &&
    isOpen &&
    dropdownPosition &&
    typeof document !== "undefined";

  // Dropdown styles using fixed positioning (rendered inside a Portal at document.body)
  const dropdownStyle: React.CSSProperties = dropdownPosition
    ? {
        position: "fixed",
        top:
          dropdownPosition.placement === "bottom"
            ? dropdownPosition.top
            : undefined,
        bottom:
          dropdownPosition.placement === "top"
            ? window.innerHeight - dropdownPosition.top
            : undefined,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: DROPDOWN_Z_INDEX,
        maxHeight: "320px",
        overflowY: "auto",
      }
    : {};

  return (
    <Box position="relative" width="100%">
      {label && (
        <>
          {hideLabel ? (
            <VisuallyHidden>
              <FormLabel {...getLabelProps()}>{label}</FormLabel>
            </VisuallyHidden>
          ) : (
            <FormLabel {...getLabelProps()}>{label}</FormLabel>
          )}
        </>
      )}
      <Box ref={containerRef} width="100%">
        {multiple ? MultiSelectInputContainer : SingleSelectInputContainer}
      </Box>

      {helperText && (
        <Text fontSize="xs" color="fg.muted" mt="xs">
          {helperText}
        </Text>
      )}

      {/* Dropdown rendered via Portal to escape overflow/stacking contexts */}
      <Portal>
        <Dropdown
          {...menuProps}
          ref={mergedDropdownRef}
          data-combobox-menu="true"
          variant={multiple ? "secondary" : dropdownVariant}
          onClick={(e) => e.stopPropagation()}
          onTouchEnd={(e: React.TouchEvent<HTMLElement>) => e.stopPropagation()}
          style={shouldShowDropdown ? dropdownStyle : undefined}
        >
          {shouldShowDropdown && (
            <>
              {filteredItems.length > 0 ? (
                <List py={multiple ? "md" : undefined}>
                  {filteredItems.map((item, index) => {
                    const isDisabled = isItemDisabled
                      ? isItemDisabled(item)
                      : false;
                    const itemProps = getItemProps({
                      item,
                      index,
                      disabled: isDisabled,
                    });

                    const enhancedItemProps = {
                      ...itemProps,
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        if (!isDisabled && itemProps.onClick)
                          itemProps.onClick(e);
                      },
                      onTouchEnd: (e: React.TouchEvent<HTMLElement>) => {
                        e.stopPropagation();
                      },
                    };

                    if (renderItem) {
                      return (
                        <ListItem
                          key={item.id}
                          isSelected={highlightedIndex === index}
                          isDisabled={isDisabled}
                          variant={multiple ? "secondary" : undefined}
                          {...enhancedItemProps}
                        >
                          {renderItem(item, index, highlightedIndex === index)}
                        </ListItem>
                      );
                    }

                    return (
                      <ListItem
                        isSelected={highlightedIndex === index}
                        isDisabled={isDisabled}
                        key={item.id}
                        px={multiple ? "2xs" : undefined}
                        variant={multiple ? "secondary" : undefined}
                        {...enhancedItemProps}
                      >
                        <Box ml="xs" />
                        {listIcon && (
                          <Box mr={isMobileModal ? "15px" : "10px"}>
                            {listIcon}
                          </Box>
                        )}
                        <Box flex="1">
                          <Text
                            isTruncated
                            mr="xs"
                            color={isDisabled ? "fg.muted" : undefined}
                            css={
                              isDisabled
                                ? undefined
                                : {
                                    '[aria-selected="true"] &': {
                                      color: hoverTextColor,
                                    },
                                  }
                            }
                            _groupHover={
                              isDisabled ? undefined : { color: hoverTextColor }
                            }
                          >
                            {item.name ?? ""}
                            {item?.secondaryText && (
                              <Text
                                as="span"
                                color={isDisabled ? "fg.muted" : "gray.600"}
                                css={
                                  isDisabled
                                    ? undefined
                                    : {
                                        '[aria-selected="true"] &': {
                                          color: hoverTextColor,
                                        },
                                      }
                                }
                              >{`, ${item.secondaryText}`}</Text>
                            )}
                          </Text>
                          {isDisabled && disabledItemText && (
                            <Text fontSize="xs" color="fg.muted">
                              {disabledItemText}
                            </Text>
                          )}
                        </Box>
                      </ListItem>
                    );
                  })}
                  {/* Load More / Loading states */}
                  {(hasNextPage || isLoading) && (
                    <>
                      {!isLoading && hasNextPage && (
                        <ListItem
                          variant="secondary"
                          isSelected={highlightedIndex === filteredItems.length}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            onLoadMore?.();
                          }}
                        >
                          <Box pl="xs" w="100%">
                            <Text textAlign="center" isTruncated mr="xs">
                              {t("Load More")}
                            </Text>
                          </Box>
                        </ListItem>
                      )}
                      {isLoading && (
                        <ListItem>
                          <Box pl="xs" w="100%">
                            <Text textAlign="center" isTruncated mr="xs">
                              {t("Loading...")}
                            </Text>
                          </Box>
                        </ListItem>
                      )}
                    </>
                  )}
                </List>
              ) : (
                <List py="md">
                  <ListItem px="2xs" variant="secondary" isDisabled>
                    <Box pl="xs" w="100%">
                      <Text isTruncated mr="xs">
                        {isLoading ? t("Fetching...") : t("No results found")}
                      </Text>
                    </Box>
                  </ListItem>
                </List>
              )}
            </>
          )}
        </Dropdown>
      </Portal>

      {/* Selected items list below input */}
      {multiple &&
        selectedItemsDisplay === "list" &&
        selectedItems.length > 0 && (
          <List mt="md">
            {selectedItems.map((item) => (
              <Box key={item.id}>
                <ListItem
                  flexDirection="row"
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  paddingBottom="2xs"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveSelectedItem(item);
                  }}
                  as="button"
                  tabIndex={0}
                >
                  <Text isTruncated flex="1" minWidth={0} title={item.name}>
                    {item.name}
                  </Text>
                  <Icon.X.Outline h="element.lg" w="element.lg" />
                </ListItem>
              </Box>
            ))}
          </List>
        )}
    </Box>
  );
}

export default ComboBox;
